import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { TeamSummaryService } from '../../services/team-summary.service';
import { DwModalService } from '@devwareapps/devware-cap';
import { openTeamMemberPicker } from '../../../shared/components/profile-tabs/team-members.component';
import { CentralDataService, DisplayTeam } from '../../../streaker-central/services/central-data.service';
import { debounceTime, filter, map, switchMap, catchError } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { ChallengeTeamEntity, ChallengeTeamMemberEntity, UserFitnessSummaryEntity } from '../../../../meta-data/app-meta-data.service';
import { TeamMembershipStatus } from '../../models/team.models';
import { teamProfilePath, userProfilePath } from '../../../shared/services/profile-routing/profile-links';
import { TeamCardMode, TeamCardSummary, summarizeTeam, memberName, memberProfile } from '../../models/team-card.models';

@Component({ selector: 'app-team-card', templateUrl: './team-card.component.html', styleUrls: ['./team-card.component.scss'] })
export class TeamCardComponent implements OnChanges, OnDestroy {
    @Input() team: DisplayTeam;
    @Input() membershipStatus: TeamMembershipStatus;
    @Input() memberCount: number = null;
    @Input() showSummary = false;
    @Input() dashboard = false;
    @Input() readOnly = false;
    @Input() mode: TeamCardMode = 'normal';
    @Input() summary: TeamCardSummary;
    @Input() activitiesStarted = false;
    @Input() collapsibleStats = false;
    @Input() statsExpanded = true;
    @Output() statsToggled = new EventEmitter<void>();
    @Input() currentUserChallengeId: number;
    members: ChallengeTeamMemberEntity[] = [];
    summaries: UserFitnessSummaryEntity[] = [];
    loading = false;
    error = '';
    private requests = new Subscription();
    private changes = new Subscription();
    private backfill = new Subscription();
    private summaryGeneration = 0;
    private lastTeamId = 0;

    constructor(private teamSummaries: TeamSummaryService, private dialogs: DwModalService, private centralData: CentralDataService) {
        this.changes.add(centralData.teamChanged$.subscribe(change => {
            if (!this.team || (change.teamId && change.teamId !== this.team.ChallengeTeamId)) { return; }
            if (change.team) { this.team = { ...this.team, ...change.team }; }
            if (change.member) {
                this.summaryGeneration++;
                const member = change.member;
                const exists = this.members.some(row => row.UserChallengeId === member.UserChallengeId);
                this.members = change.removed ? this.members.filter(row => row.UserChallengeId !== member.UserChallengeId)
                    : exists ? this.members.map(row => row.UserChallengeId === member.UserChallengeId ? { ...row, ...member } : row)
                    : member.UserChallenge ? [...this.members, member] : this.members;
                this.summary = summarizeTeam(this.members, this.summaries);

            }
        }));
        this.backfill = centralData.teamChanged$.pipe(filter(change => (change.kind === 'membership' || !!change.member) && !!this.team && (!change.teamId || change.teamId === this.team.ChallengeTeamId)),
            debounceTime(0), switchMap(() => {
                const id = this.team.ChallengeTeamId, generation = this.summaryGeneration;
                return this.teamSummaries.load([id], this.activitiesStarted).pipe(map(rows => ({ id, generation, summary: rows[id] })), catchError(() => of(null)));
            })).subscribe(result => {
                if (result?.summary && this.team?.ChallengeTeamId === result.id && this.summaryGeneration === result.generation) { this.summary = result.summary; this.members = result.summary.members; this.summaries = result.summary.summaries; }
            });
    }
    addMember(): void {
        if (this.readOnly || !this.membership.isAdmin) { return; }
        this.requests.add(openTeamMemberPicker(this.dialogs, {
            teamId: this.team.ChallengeTeamId, challengeId: this.team.ChallengeDefinitionId, members: this.members,
            onChanged: change => this.centralData.updateMember(change.member, change.removed),
        }).subscribe());
    }
    memberName = memberName;
    memberProfile = memberProfile;
    memberLink(member: ChallengeTeamMemberEntity): string {
        const slug = memberProfile(member)?.ProfileSlug;
        return slug ? userProfilePath(slug) : null;
    }
    get membership(): TeamMembershipStatus {
        if (this.membershipStatus) { return this.membershipStatus; }
        const own = this.currentUserChallengeId ? this.members.find(member => member.UserChallengeId === this.currentUserChallengeId) : null;
        return { isMember: !!own, isAdmin: own?.IsTeamAdmin === true, hasPendingRequest: false };
    }
    get memberLabel(): string {
        const count = this.memberCount == null ? this.members.length : this.memberCount;
        return `${count} ${count === 1 ? 'member' : 'members'}`;
    }
    get initials(): string { return this.nameInitials(this.team?.TeamName); }
    get profileLink(): string {
        const team = this.team;
        return team?.TeamSlug && team?.ChallengeDefinitionId ? teamProfilePath(team.ChallengeDefinitionId, team.TeamSlug) : '/streaker-central/teams/id/' + team.ChallengeTeamId;
    }
    nameInitials(name: string): string { return (name || 'Team').trim().split(/\s+/).map(word => word[0]).slice(0, 2).join('').toUpperCase(); }
    get duration(): string { const seconds = this.summary ? this.summary.durationSeconds : this.total('TotalDurationSeconds'); return seconds !== null && seconds !== '—' ? (Number(seconds) / 3600).toFixed(1) + ' hrs' : '—'; }
    total(field: 'TotalActivities' | 'TotalDurationSeconds' | 'TotalMissedDays'): number | string {
        if (this.error) { return '—'; }
        if (this.summary) { const keys: Record<typeof field, 'activities' | 'durationSeconds' | 'missed'> = { TotalActivities: 'activities', TotalDurationSeconds: 'durationSeconds', TotalMissedDays: 'missed' }; const key = keys[field]; return this.summary[key] ?? '—'; }
        return this.summaries.length ? this.summaries.reduce((sum, row) => sum + (Number(row[field]) || 0), 0) : '—';
    }
    get longest(): number | string { return this.error ? '—' : this.summary ? this.summary.longest ?? '—' : this.summaries.length ? Math.max(...this.summaries.map(row => Number(row.LongestStreakDays) || 0)) : '—'; }
    ngOnChanges(changes?: SimpleChanges): void {
        if (this.lastTeamId === this.team?.ChallengeTeamId && changes && !changes.summary && !changes.showSummary && !changes.activitiesStarted) { return; }
        this.lastTeamId = this.team?.ChallengeTeamId || 0;
        this.requests.unsubscribe(); this.requests = new Subscription(); this.members = []; this.summaries = []; this.error = '';
        if (this.summary) { this.members = this.summary.members; this.summaries = this.summary.summaries; this.loading = false; return; }
        if (!this.showSummary || !this.team?.ChallengeTeamId) { this.loading = false; return; }
        this.loading = true;
        this.requests.add(this.teamSummaries.load([this.team.ChallengeTeamId], this.activitiesStarted).subscribe(results => {
            const summary = results[this.team.ChallengeTeamId];
            this.members = summary.members;
            this.summaries = summary.summaries;
            if (this.activitiesStarted && (summary.activities === null || summary.durationSeconds === null || summary.missed === null || summary.longest === null)) { this.error = 'Some team stats are unavailable.'; }
            this.loading = false;
        }, () => { this.loading = false; this.error = 'Team summary unavailable.'; }));
    }
    ngOnDestroy(): void { this.requests.unsubscribe(); this.changes.unsubscribe(); this.backfill.unsubscribe(); }
}
